import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { trackTfnClick } from '../../../common/GTMScripts';
import { STATIC_RESULT_CARDS } from '../../../data/staticResultCards';
import { DownloadUploadSpeedIcon, UnlimitedDataIcon, CustomerSupportIcon, LifeLockIcon } from '../../../images/svg';
import './InternetPlans.scss';

const FEATURE_ICONS = [DownloadUploadSpeedIcon, UnlimitedDataIcon, CustomerSupportIcon, LifeLockIcon];

/**
 * A single static card. Mirrors the markup and class names ProductPlanCard emits so the
 * shared stylesheet applies unchanged, minus the price header and the cart button - neither
 * of which has any meaning for a product the customer cannot buy here.
 */
const StaticPlanCard = ({ card, phoneNumber, productCategory }) => {
  const { t } = useTranslation();
  const [viewDetails, setViewDetails] = useState(false);
  const features = card.featureKeys.map((key) => t(key));
  const visibleFeatures = viewDetails ? features : features.slice(0, 2);

  return (
    <div className={`plan-single ${card.highlighted ? 'highlighted centered' : ''}`}>
      <div className="plan-single-inner">
        <div className="elnk-plan-top-bar">{t(card.labelKey)}</div>

        <div className="plans-header">
          <h4 className="plans-header__title">{t(card.titleKey)}</h4>
        </div>

        <div className="plans-body">
          <ul className={`elnk-plan-feature-list ${viewDetails ? 'elnk-plan-feature-list-open' : ''}`}>
            {visibleFeatures.map((feature, index) => {
              const Icon = FEATURE_ICONS[index];
              return (
                <li key={`${card.id}-feature-${index}`} className="plan-feature-item">
                  {Icon && <span className="plan-feature-icon"><Icon /></span>}
                  <p>{feature}</p>
                </li>
              );
            })}
          </ul>
          <div className="view-details-link" onClick={() => setViewDetails(!viewDetails)}>
            {viewDetails ? t('common.hideDetails') : t('common.viewDetails')}
          </div>
        </div>

        <div className="plans-footer">
          <div className="agent-call-button">
            <a
              href={`tel:${phoneNumber}`}
              className="blue-button elnk-tfn-link"
              onClick={() => trackTfnClick({ productCategory, speedTier: card.speedTier })}
            >
              <i className="fa-solid fa-phone-volume"></i>
              <span className="phone-span elnk-tfn-tag">{phoneNumber}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

StaticPlanCard.propTypes = {
  card: PropTypes.object.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  productCategory: PropTypes.string.isRequired,
};

/**
 * ELNKWEB-3210 - the 3-card results view, shared by the two states the requirements doc
 * changes: 5G-only serviceable, and not serviceable at all. The doc requires those states to
 * be identical ("no separate copy variant for this state"), so they render the same
 * component with the same hardcoded cards; only the surrounding page differs.
 *
 * The cards are deliberately NOT routed through InternetPlans' own pipeline. That pipeline
 * sorts by speed, renders prices, and - crucially - swaps the call CTA for an add-to-cart
 * button whenever the call centre is closed, since showCallNow is time-of-day dependent. Two
 * of the three products shown are never available at the address, so no purchase path may be
 * reachable under any condition. Here the phone CTA is unconditional.
 */
const StaticResultsView = ({ phoneNumber, address, onResetAddress, productCategory }) => {
  const { t } = useTranslation();

  return (
    <section className="elnk-internet-plans-section">
      <div className="internet-result-heading internetOuterClass">
        <div className="elink-container">
          <div className="elnk-internet-heading">
            <h1 className="elnk-internet-heading__title">{t('products.titleEarthlinkAvailable')}</h1>
            {address && (
              <p className="elnk-internet-heading__address">
                <span className="address-txt">{address}</span>
                <button className="address-btn" type="button" onClick={onResetAddress}>
                  <i className="fa-solid fa-pen-to-square address-edit-icon" aria-hidden="true"></i> {t('products.editAddress')}
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="all-single-plans internetOuterClass">
        <div className="elink-container">
          {/* .plans-carousel is the selector the card chrome is nested under in
              InternetPlans.scss; .plans-carousel-row inside it is what PlanSlider emits for a
              three-card set. Both are needed for the cards to pick up their styling. */}
          <div className="plans-carousel">
            <section className="plans-carousel-row">
              {STATIC_RESULT_CARDS.map((card) => (
                <StaticPlanCard
                  key={card.id}
                  card={card}
                  phoneNumber={phoneNumber}
                  productCategory={productCategory}
                />
              ))}
            </section>
          </div>
        </div>
      </div>
    </section>
  );
};

StaticResultsView.propTypes = {
  phoneNumber: PropTypes.string.isRequired,
  address: PropTypes.string,
  onResetAddress: PropTypes.func.isRequired,
  productCategory: PropTypes.string.isRequired,
};

export default StaticResultsView;
